/**
 * Fotos de evolução (módulo `evolution_photos`).
 *
 * As fotos são anexos do paciente (núcleo `patient_attachments`) com
 * context = 'evolution', kind = 'image' e, em `meta`, a região e o ângulo:
 * { "region": "abdomen", "angle": "front" }. Os dois são opcionais; uma foto
 * sem rótulo é válida e aparece como "Sem região" / "Sem ângulo".
 *
 * As regiões são as do mapa corporal (`bodyRegions.js`), sem a vista: a chave
 * `thigh_right` vale para a coxa direita vista de frente ou de costas, porque o
 * ângulo da foto já diz de onde ela foi tirada. Os ângulos são um catálogo
 * fixo, espelhado em `public/api/patients/attachments/EvolutionPhotoMeta.php`.
 *
 * Funções puras, usadas pelo card, pelos popups e pela aba Saúde.
 */

import dayjs from 'dayjs';
import { BODY_REGIONS } from '../../../components/bodySilhouette/bodyRegions';

export const EVOLUTION_PHOTO_CONTEXT = 'evolution';

export const EVOLUTION_PHOTO_ANGLES = Object.freeze([
  { value: 'front', label: 'Frente' },
  { value: 'back', label: 'Costas' },
  { value: 'right', label: 'Lado direito' },
  { value: 'left', label: 'Lado esquerdo' },
  { value: 'detail', label: 'Detalhe' },
]);

export const NO_REGION_LABEL = 'Sem região';
export const NO_ANGLE_LABEL = 'Sem ângulo';

// Uma entrada por chave: a primeira ocorrência (vista de frente) dá o rótulo,
// sem o sufixo "(posterior)" das vistas de costas.
export const EVOLUTION_PHOTO_REGIONS = Object.freeze(
  BODY_REGIONS.reduce((list, item) => {
    if (!list.some((region) => region.key === item.key)) {
      list.push({ key: item.key, label: item.label.replace(/\s*\(posterior\)/, '') });
    }
    return list;
  }, [])
);

const ANGLE_ORDER = EVOLUTION_PHOTO_ANGLES.reduce((map, item, position) => {
  map[item.value] = position;
  return map;
}, {});

export function getRegionLabel(key) {
  const found = EVOLUTION_PHOTO_REGIONS.find((item) => item.key === key);
  return found ? found.label : NO_REGION_LABEL;
}

export function getAngleLabel(value) {
  const found = EVOLUTION_PHOTO_ANGLES.find((item) => item.value === value);
  return found ? found.label : NO_ANGLE_LABEL;
}

export function getPhotoRegion(photo) {
  return (photo && photo.meta && photo.meta.region) || '';
}

export function getPhotoAngle(photo) {
  return (photo && photo.meta && photo.meta.angle) || '';
}

/** "Abdômen · Frente", "Abdômen", "Frente" ou "Sem identificação". */
export function photoLabel(photo) {
  const parts = [];
  if (getPhotoRegion(photo)) parts.push(getRegionLabel(getPhotoRegion(photo)));
  if (getPhotoAngle(photo)) parts.push(getAngleLabel(getPhotoAngle(photo)));
  return parts.length > 0 ? parts.join(' · ') : 'Sem identificação';
}

export function formatPhotoDate(photo) {
  return photo && photo.documentDate ? dayjs(photo.documentDate).format('DD/MM/YYYY') : '';
}

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/** "12 de março de 2026", para o modo apresentação. */
export function formatPhotoDateLong(photo) {
  if (!photo || !photo.documentDate) return '';
  const date = dayjs(photo.documentDate);
  return `${date.date()} de ${MONTHS[date.month()]} de ${date.year()}`;
}

function plural(count, singular, pluralForm) {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/**
 * Tempo entre as duas fotos, em linguagem de paciente: "Mesmo dia",
 * "12 dias depois", "3 semanas depois", "4 meses depois", "1 ano e 2 meses
 * depois". Vazio sem as duas datas ou com "depois" anterior ao "antes".
 */
export function describeElapsed(before, after) {
  if (!before || !after || !before.documentDate || !after.documentDate) return '';
  const start = dayjs(before.documentDate).startOf('day');
  const end = dayjs(after.documentDate).startOf('day');
  const days = end.diff(start, 'day');
  if (days < 0) return '';
  if (days === 0) return 'Mesmo dia';
  if (days < 14) return `${plural(days, 'dia', 'dias')} depois`;
  const months = end.diff(start, 'month');
  if (months === 0) return `${plural(Math.round(days / 7), 'semana', 'semanas')} depois`;
  if (months < 12) return `${plural(months, 'mês', 'meses')} depois`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const yearsText = plural(years, 'ano', 'anos');
  return rest > 0 ? `${yearsText} e ${plural(rest, 'mês', 'meses')} depois` : `${yearsText} depois`;
}

/** Só os anexos do módulo. */
export function filterEvolutionPhotos(attachments) {
  return (attachments || []).filter((item) => item && item.context === EVOLUTION_PHOTO_CONTEXT);
}

/**
 * Campos do anexo a partir de região, ângulo e nota: `meta` só com o que foi
 * preenchido e o título com o rótulo legível (é o que o visualizador e a
 * exportação mostram).
 */
export function buildPhotoFields(value) {
  const region = (value && value.region) || '';
  const angle = (value && value.angle) || '';
  const meta = {};
  if (region) meta.region = region;
  if (angle) meta.angle = angle;

  return {
    meta: Object.keys(meta).length > 0 ? JSON.stringify(meta) : '',
    title: photoLabel({ meta }),
    note: ((value && value.note) || '').trim(),
  };
}

// Mais antiga primeiro; empate de data pela ordem de envio
export function sortByDateAsc(photos) {
  return (photos || []).slice().sort((a, b) => {
    const dateA = a.documentDate || '';
    const dateB = b.documentDate || '';
    if (dateA !== dateB) return dateA < dateB ? -1 : 1;
    return Number(a.id) - Number(b.id);
  });
}

// Dentro de uma sessão: por região (ordem do catálogo), ângulo e id
const regionPosition = (key) => {
  const position = EVOLUTION_PHOTO_REGIONS.findIndex((item) => item.key === key);
  return position === -1 ? EVOLUTION_PHOTO_REGIONS.length : position;
};

function comparePhotosInSession(a, b) {
  const regionA = regionPosition(getPhotoRegion(a));
  const regionB = regionPosition(getPhotoRegion(b));
  if (regionA !== regionB) return regionA - regionB;
  const angleA = ANGLE_ORDER[getPhotoAngle(a)] ?? EVOLUTION_PHOTO_ANGLES.length;
  const angleB = ANGLE_ORDER[getPhotoAngle(b)] ?? EVOLUTION_PHOTO_ANGLES.length;
  if (angleA !== angleB) return angleA - angleB;
  return Number(a.id) - Number(b.id);
}

/**
 * Sessões fotográficas: as fotos agrupadas por data e atendimento, da mais
 * recente para a mais antiga.
 *
 * @returns {Array<{key: string, date: string, appointmentId: number|null, photos: Array}>}
 */
export function groupPhotosBySession(photos) {
  const groups = {};

  (photos || []).forEach((photo) => {
    const key = `${photo.documentDate || ''}|${photo.appointmentId || ''}`;
    if (!groups[key]) {
      groups[key] = { key, date: photo.documentDate || '', appointmentId: photo.appointmentId || null, photos: [] };
    }
    groups[key].photos.push(photo);
  });

  return Object.values(groups)
    .map((group) => ({ ...group, photos: group.photos.slice().sort(comparePhotosInSession) }))
    .sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? 1 : -1;
      return Number(b.appointmentId || 0) - Number(a.appointmentId || 0);
    });
}

/** Regiões presentes nas fotos do paciente, na ordem do catálogo. */
export function regionsInPhotos(photos) {
  const present = new Set((photos || []).map(getPhotoRegion).filter(Boolean));
  return EVOLUTION_PHOTO_REGIONS.filter((item) => present.has(item.key));
}

/** Ângulos presentes nas fotos do paciente, na ordem do catálogo. */
export function anglesInPhotos(photos) {
  const present = new Set((photos || []).map(getPhotoAngle).filter(Boolean));
  return EVOLUTION_PHOTO_ANGLES.filter((item) => present.has(item.value));
}

export function applyFilters(photos, filters) {
  const region = (filters && filters.region) || '';
  const angle = (filters && filters.angle) || '';
  return (photos || []).filter((photo) => {
    if (region && getPhotoRegion(photo) !== region) return false;
    if (angle && getPhotoAngle(photo) !== angle) return false;
    return true;
  });
}

/**
 * Enquadramentos compatíveis para o controle deslizante: mesma região e mesmo
 * ângulo (os dois rotulados). Fotos sem rótulo só comparam lado a lado.
 */
export function isSliderCompatible(a, b) {
  if (!a || !b) return false;
  const region = getPhotoRegion(a);
  const angle = getPhotoAngle(a);
  return Boolean(region) && Boolean(angle) && region === getPhotoRegion(b) && angle === getPhotoAngle(b);
}

/** Par ordenado para comparar: [antes, depois]. */
export function orderPair(a, b) {
  return sortByDateAsc([a, b]);
}

/**
 * Par em comparação a partir da escolha guardada ({ before, after } com ids,
 * ou null) e das fotos no filtro (mais antiga primeiro). Escolha ausente ou
 * inválida (foto fora do filtro ou apagada) cai no padrão: mais antiga como
 * "Antes" e mais recente como "Depois". Com uma só foto, ela fica em "Antes"
 * e "Depois" fica vazio.
 *
 * @returns {{before: object|null, after: object|null}}
 */
export function resolveComparison(selection, photosAsc) {
  const list = photosAsc || [];
  if (list.length === 0) return { before: null, after: null };
  if (list.length === 1) return { before: list[0], after: null };

  const find = (id) => (id == null ? null : list.find((photo) => String(photo.id) === String(id)) || null);
  const before = selection ? find(selection.before) : null;
  const after = selection ? find(selection.after) : null;
  if (before && after && before.id !== after.id) return { before, after };

  return { before: list[0], after: list[list.length - 1] };
}

/**
 * Coloca `photo` no lado `side` ('before' | 'after') do par. Se ela já estava
 * no outro lado, os lados se invertem. Datas invertidas (Antes mais nova que
 * Depois) voltam à ordem cronológica; datas iguais ou ausentes respeitam a
 * escolha.
 */
export function placeInComparison(pair, side, photo) {
  const current = side === 'before' ? pair.before : pair.after;
  const other = side === 'before' ? pair.after : pair.before;
  if (current && current.id === photo.id) return pair;

  let next;
  if (other && other.id === photo.id) {
    next = { before: pair.after, after: pair.before };
  } else {
    next = side === 'before' ? { before: photo, after: pair.after } : { before: pair.before, after: photo };
  }

  const inverted = next.before && next.after
    && next.before.documentDate && next.after.documentDate
    && next.before.documentDate > next.after.documentDate;
  if (inverted) {
    const [before, after] = orderPair(next.before, next.after);
    return { before, after };
  }
  return next;
}
